/**
 * BookingService CORREGIDO - Post-fix Supabase
 * 
 * ✅ Resuelve error de constraint "valid_tipo_consulta"
 * ✅ Usa funciones RPC creadas en SOLUCION_DEFINITIVA_2026.sql
 * ✅ Compatible con valores exactos del frontend
 */

import { supabase } from '../lib/supabase/client';
import { toSantiagoISO } from '../lib/utils/dateUtils';
import { RATE_LIMIT_MESSAGE, PATIENT_CONFLICT_MESSAGE } from './patient-validation.service';
import type {
  SelectedService,
  SelectedTimeSlot,
  PatientData,
  MetodoPagoDeclarado
} from '../components/Booking/BookingContext';
import type { TherapistExtended } from '../types';

export interface BookingData {
  therapist: TherapistExtended;
  service: SelectedService;
  timeSlot: SelectedTimeSlot;
  patientData: PatientData;
  // Intención de pago declarada por el paciente al agendar (Fase 0.5) — no un
  // pago real. Opcional para no romper callers que aún no la recolectan
  // (ej. ExistingPatientBooking.tsx); el servicio usa 'tarjeta' (precio de lista) por defecto.
  metodoPagoDeclarado?: MetodoPagoDeclarado;
  // FUNC-126: id del paciente existente ya resuelto por search_patient_by_rut.
  // Si viene, processExistingPatientBooking no llama create_patient_simple
  // (que exige email, y este paciente no lo tipeó porque ya está en su ficha).
  existingPatientId?: string;
}

// FUNC-114 (Ley 21.719): identificador de versión del texto legal vigente en
// cada checkbox — NO es el contenido del documento (eso lo define el negocio
// aparte). Subir estos strings cuando cambie el texto que ve el paciente.
export const CONSENT_VERSIONS = {
  datosClinicos: 'v1-2026-09',
  contactoMarketing: 'v1-2026-09',
} as const;

export interface BookingResult {
  success: boolean;
  bookingId: string;
  patientId?: string;
  consultaId?: string;
  meetLink?: string;
  error?: string;
}

// FUNC-121: mensajes que el paciente puede ver tal cual. Todo lo demás (error
// crudo de Supabase / PostgREST) se reemplaza por el genérico en el borde del
// servicio — nunca se expone schema ni mensajes internos de la RPC.
export const SLOT_TAKEN_MESSAGE =
  'Ese horario ya no está disponible. Elegí otro, por favor.';
export const BOOKING_BLOCKED_MESSAGE =
  'No es posible agendar esta cita. Comunicate con el equipo de Conectemos Chile.';
export const BOOKING_GENERIC_ERROR_MESSAGE =
  'No pudimos procesar tu cita. Intentá de nuevo o escribinos al +56 9 7800 9119.';
const THERAPIST_LOOKUP_ERROR_MESSAGE =
  'No se pudo verificar el terapeuta. Intente nuevamente.';
const THERAPIST_UNAVAILABLE_MESSAGE =
  'El terapeuta seleccionado no está disponible. Intente nuevamente.';

const PATIENT_FACING_MESSAGES: ReadonlySet<string> = new Set([
  RATE_LIMIT_MESSAGE,
  PATIENT_CONFLICT_MESSAGE,
  SLOT_TAKEN_MESSAGE,
  BOOKING_BLOCKED_MESSAGE,
  BOOKING_GENERIC_ERROR_MESSAGE,
  THERAPIST_LOOKUP_ERROR_MESSAGE,
  THERAPIST_UNAVAILABLE_MESSAGE,
]);

export function isPatientFacingBookingError(message: string): boolean {
  return PATIENT_FACING_MESSAGES.has(message);
}

function toPatientFacingError(error: unknown): string {
  const message = error instanceof Error ? error.message : '';
  return isPatientFacingBookingError(message) ? message : BOOKING_GENERIC_ERROR_MESSAGE;
}

export class BookingServiceFixed {
  /**
   * Crear paciente usando función RPC corregida
   */
  static async createPatientRPC(patientData: PatientData, therapistId: string): Promise<string> {
    console.log('👤 Creando paciente con función RPC corregida...');
    
    // ✅ FIX: Limpiar formato de teléfono - máximo 12 caracteres
    const cleanPhone = (patientData.telefono || '')
      .replace(/\+56/, '') // Quitar prefijo +56
      .replace(/[^0-9]/g, '') // Solo números
      .substring(0, 12); // Máximo 12 caracteres
    
    const { data: patientId, error } = await supabase
      .rpc('create_patient_simple', {
        p_nombre: patientData.nombre,
        p_apellido: patientData.apellido,
        p_email: patientData.email,
        p_telefono: cleanPhone,
        p_rut: patientData.rut,
        p_terapeuta_id: therapistId,
        p_genero: patientData.genero || null,
        p_nombre_pareja: patientData.nombrePareja?.trim() || null,
        p_direccion: patientData.direccion?.trim() || null,
      });

    if (error) {
      console.error('❌ Error en RPC create_patient_simple:', error);
      // SEC-059: rate-limit server-side (PT429) — mensaje propio, sin el
      // prefijo genérico (no es un error crudo de Supabase, es nuestro).
      if (error.code === 'PT429' || error.hint === 'rate_limited') {
        throw new Error(RATE_LIMIT_MESSAGE);
      }
      throw new Error(`Error creando paciente: ${error.message}`);
    }

    if (!patientId) {
      // SEC-062: RUT nuevo + email ya registrado → la RPC devuelve NULL.
      throw new Error(PATIENT_CONFLICT_MESSAGE);
    }

    console.log('✅ Paciente creado/encontrado con ID:', patientId);
    return patientId;
  }

  /**
   * FUNC-114 (Ley 21.719) — Registrar consentimientos versionados del paciente
   * NUEVO. Solo se llama desde processBooking (paciente nuevo): el paciente
   * existente ya tiene su consentimiento de datos clínicos registrado desde
   * que se creó, y ExistingPatientBooking.tsx no ofrece checkbox de marketing.
   * Se llama después de crear el paciente y antes de crear la consulta —
   * el consentimiento debe quedar registrado antes de procesar el motivo de
   * consulta (dato de salud) de esa cita.
   *
   * La RPC resuelve el paciente por RUT server-side (no recibe patientId
   * crudo) — hallazgo de la revisión `security`: un UUID mandado por el
   * cliente es forjable vía search_patient_by_rut (ya público), así que no
   * protege nada como identificador de ownership.
   */
  static async saveConsentimientosRPC(patientData: PatientData): Promise<void> {
    const { error } = await supabase.rpc('save_patient_consents', {
      p_paciente_rut: patientData.rut,
      p_acepta_datos_clinicos: patientData.consentimientoDatosClinicos,
      p_version_datos_clinicos: CONSENT_VERSIONS.datosClinicos,
      p_acepta_contacto_marketing: patientData.consentimientoContactoMarketing,
      p_version_contacto_marketing: CONSENT_VERSIONS.contactoMarketing,
    });

    if (error) {
      console.error('❌ Error en RPC save_patient_consents:', error);
      throw new Error(`Error registrando consentimiento: ${error.message}`);
    }
  }

  /**
   * Crear consulta usando función RPC corregida
   */
  static async createConsultationRPC(
    patientId: string,
    therapistId: string,
    timeSlot: SelectedTimeSlot,
    service: SelectedService,
    // FUNC-131: fallback conservador = tarjeta (precio de lista) si un caller omite el método.
    metodoPagoDeclarado: MetodoPagoDeclarado = 'tarjeta'
  ): Promise<string> {
    console.log('📅 Creando consulta con función RPC corregida...');
    
    const tipoConsultaMapping: { [key: string]: string } = {
      'individual-50': 'individual',
      'individual-90': 'individual',
      'parejas-50': 'pareja',
      'parejas-90': 'pareja',
      'familiar-50': 'familiar',
      'familiar-90': 'familiar',
      'pareja-50': 'pareja',
      'pareja-90': 'pareja',
      'familia-50': 'familiar',
      'familia-90': 'familiar'
    };

    // ExistingPatientBooking generates IDs as "type-duration-modality" (e.g. "pareja-50-presencial").
    // Strip the modality suffix so the mapping key always matches "type-duration".
    // Incluye 'domicilio' (feature domicilio, solo pareja/90min) — sin esto, un id
    // "pareja-90-domicilio" no matchea nada en tipoConsultaMapping y cae al fallback
    // 'individual', guardando mal el tipo_consulta real de la cita.
    const serviceKey = service.id.replace(/-(presencial|online|domicilio)$/, '');
    const tipoConsulta = tipoConsultaMapping[serviceKey] || 'individual';

    // FIX TIMEZONE: Offset dinámico America/Santiago (CLT=-04:00 invierno, CLST=-03:00 verano)
    // No hardcodear -03:00 — Chile cambia de offset con DST y causaba desfase de 1h en invierno
    const { toSantiagoISO } = await import('../lib/utils/dateUtils');
    const fechaHora = toSantiagoISO(timeSlot.date, timeSlot.time);
    
    const { data: consultationId, error } = await supabase
      .rpc('create_consultation', {
        patient_id: patientId,
        therapist_id: therapistId,
        fecha_hora: fechaHora,
        duracion: service.duration,
        tipo_consulta: tipoConsulta,    // ✅ Valor que pasa el constraint
        // FIX: antes colapsaba cualquier valor no-'presencial' a 'online', lo que
        // guardaba 'domicilio' como 'online' en BD. Pasar el valor real tal cual.
        modalidad: service.modality,
        // ARQ-012 (2026-09-07): create_consultation es una función ÚNICA — se
        // fusionaron los 2 overloads (público + staff). El cuerpo decide la
        // rama por identidad (auth.uid() == terapeuta de therapist_id), no por
        // el set de parámetros. Desde acá, sin sesión de terapeuta → rama
        // pública: valor_consulta/descuento se IGNORAN (precio server-side,
        // SEC-022) y p_notas_terapeuta no aplica. Ya no hace falta mandar
        // `descuento` para desambiguar (era el fix de FUNC-052/079/103).
        valor_consulta: service.price,
        p_metodo_pago_declarado: metodoPagoDeclarado
      });

    if (error) {
      console.error('❌ Error en RPC create_consultation:', error);
      // FUNC-102: el trigger trg_consultas_rechaza_paciente_bloqueado rechaza el
      // INSERT si el paciente está bloqueado. En el wizard el aviso ya se mostró
      // en PatientVerification; esto cubre un cliente scripteado que salteó ese
      // chequeo — mensaje genérico, sin filtrar el error crudo.
      const rawMessage = (error.message ?? '').toLowerCase();
      if (rawMessage.includes('bloquead')) {
        throw new Error(BOOKING_BLOCKED_MESSAGE);
      }
      // FUNC-121: la RPC rechaza con 'Horario no disponible: …' (bloqueo de
      // agenda o superposición). El detalle (motivo del bloqueo) no se muestra.
      if (rawMessage.includes('horario no disponible')) {
        throw new Error(SLOT_TAKEN_MESSAGE);
      }
      throw new Error(`Error creando consulta: ${error.message}`);
    }

    console.log('✅ Consulta creada con ID:', consultationId);

    // ✅ CRÍTICO: Limpiar cache del calendario para reflejar el slot ocupado
    try {
      const { CalendarService } = await import('./calendar-production.service');
      CalendarService.clearCacheForDate(therapistId.toString(), timeSlot.date);
      CalendarService.clearCache(); // Limpiar cache global también
      console.log('🧹 Cache de calendario limpiado después de crear consulta');
      
      // ✅ NOTIFICACIÓN GLOBAL: Avisar a todos los calendarios activos
      if (typeof window !== 'undefined' && (window as any).notifyBookingCompleted) {
        (window as any).notifyBookingCompleted(therapistId.toString(), timeSlot.date);
      }
    } catch (error) {
      console.warn('⚠️ Error limpiando cache de calendario:', error);
    }
    
    return consultationId;
  }

  /**
   * Obtener ID real del terapeuta por slug.
   * Falla explícitamente si el slug no existe — no usar fallback silencioso
   * que guardaría la consulta con el UUID equivocado.
   */
  static async getRealTherapistId(therapistSlug: string): Promise<string> {
    const { data: therapist, error } = await supabase
      .from('terapeutas')
      .select('id, slug')
      .eq('slug', therapistSlug)
      .maybeSingle();

    if (error) {
      console.error('❌ Error buscando terapeuta por slug:', error.code);
      throw new Error(THERAPIST_LOOKUP_ERROR_MESSAGE);
    }

    if (!therapist) {
      console.error('❌ Slug no encontrado en terapeutas:', therapistSlug);
      throw new Error(THERAPIST_UNAVAILABLE_MESSAGE);
    }

    return therapist.id;
  }

  /**
   * Verificar disponibilidad (mantenido del servicio anterior) 
   */
  static async checkAvailability(
    therapistId: string,
    date: string,
    time: string
  ): Promise<boolean> {
    try {
      const realTherapistId = await this.getRealTherapistId(therapistId);
      const fechaHora = toSantiagoISO(date, time);

      const { data, error } = await supabase
        .rpc('check_consulta_slot_free', {
          p_terapeuta_id: realTherapistId,
          p_fecha_hora: fechaHora,
        });

      if (error) {
        console.log('⚠️ Error verificando disponibilidad, asumiendo disponible');
        return true;
      }

      return data ?? true;
    } catch (error) {
      console.error('❌ Error en checkAvailability:', error);
      return true;
    }
  }

  /**
   * Procesar booking para paciente existente (sin descuento)
   */
  static async processExistingPatientBooking(bookingData: BookingData): Promise<BookingResult> {
    const bookingId = `CC-EX-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    
    try {
      console.log('🚀 INICIANDO processBooking para PACIENTE EXISTENTE con ID:', bookingId);
      
      // 1. Verificar disponibilidad final
      const isAvailable = await this.checkAvailability(
        bookingData.therapist.id,
        bookingData.timeSlot.date,
        bookingData.timeSlot.time
      );

      if (!isAvailable) {
        console.warn('⚠️ Horario no disponible para paciente existente');
        throw new Error(SLOT_TAKEN_MESSAGE);
      }

      // 2. Obtener ID real del terapeuta
      const realTherapistId = await this.getRealTherapistId(bookingData.therapist.id);

      // 3. Crear o obtener paciente (debería existir ya). FUNC-126: con
      // existingPatientId se usa directo — create_patient_simple resolvería
      // el mismo id por RUT, pero exige un email que este paciente no tipeó.
      const patientId = bookingData.existingPatientId ?? await this.createPatientRPC(
        bookingData.patientData,
        realTherapistId
      );

      // 4. Crear consulta
      const consultationId = await this.createConsultationRPC(
        patientId,
        realTherapistId,
        bookingData.timeSlot,
        bookingData.service,
        bookingData.metodoPagoDeclarado
      );

      const meetLink = await this.resolveMeetLink(realTherapistId, bookingData.service.modality);

      console.log('🎉 BOOKING PACIENTE EXISTENTE COMPLETADO:', {
        bookingId,
        patientId,
        consultationId,
        meetLink,
      });

      return {
        success: true,
        bookingId,
        patientId,
        consultaId: consultationId,
        meetLink,
      };

    } catch (error) {
      console.error('💥 ERROR en processExistingPatientBooking:', error);
      return {
        success: false,
        bookingId,
        error: toPatientFacingError(error)
      };
    }
  }

  /**
   * Procesar booking completo con servicios corregidos (pacientes nuevos)
   */
  static async processBooking(bookingData: BookingData): Promise<BookingResult> {
    const bookingId = `CC-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    
    try {
      console.log('🚀 INICIANDO processBooking CORREGIDO con ID:', bookingId);
      
      // 1. Verificar disponibilidad final
      const isAvailable = await this.checkAvailability(
        bookingData.therapist.id,
        bookingData.timeSlot.date,
        bookingData.timeSlot.time
      );

      if (!isAvailable) {
        throw new Error(SLOT_TAKEN_MESSAGE);
      }

      // 2. Obtener ID real del terapeuta
      const realTherapistId = await this.getRealTherapistId(bookingData.therapist.id);

      // 3. Crear paciente usando RPC corregida
      const patientId = await this.createPatientRPC(
        bookingData.patientData,
        realTherapistId
      );

      // 3.5. Registrar consentimientos versionados (FUNC-114, Ley 21.719) —
      // antes de crear la consulta, que ya incluye motivo de consulta (dato de salud).
      await this.saveConsentimientosRPC(bookingData.patientData);

      // 4. Crear consulta usando RPC corregida
      const consultationId = await this.createConsultationRPC(
        patientId,
        realTherapistId,
        bookingData.timeSlot,
        bookingData.service,
        bookingData.metodoPagoDeclarado
      );

      const meetLink = await this.resolveMeetLink(realTherapistId, bookingData.service.modality);

      console.log('🎉 BOOKING COMPLETADO EXITOSAMENTE:', {
        bookingId,
        patientId,
        consultationId,
        meetLink,
      });

      return {
        success: true,
        bookingId,
        patientId,
        consultaId: consultationId,
        meetLink,
      };

    } catch (error) {
      console.error('💥 ERROR en processBooking CORREGIDO:', error);

      return {
        success: false,
        bookingId,
        error: toPatientFacingError(error)
      };
    }
  }

  /**
   * Si la consulta es online, devuelve la meet_url del terapeuta para la
   * pantalla de éxito y el correo de confirmación.
   * FUNC-120: el link ya lo persiste server-side el trigger
   * trg_consultas_set_meet_link al INSERT — acá no se escribe consultas
   * (el UPDATE como anon daba 401 y la fila quedaba con meet_link NULL).
   * No falla el booking si esta lectura falla.
   */
  private static async resolveMeetLink(
    therapistId: string,
    modality: string
  ): Promise<string | undefined> {
    if (modality !== 'online') return undefined;

    try {
      const { data } = await supabase
        .from('terapeutas')
        .select('meet_url')
        .eq('id', therapistId)
        .single();

      return data?.meet_url || undefined;
    } catch {
      return undefined;
    }
  }
}

export { BookingServiceFixed as BookingService };
